import {objectResolver} from 'airship/utils/resolvers'
import {ResolveError} from 'airship/utils/resolvers/errors'
import {Resolver} from 'airship/utils/resolvers/types'

// Must be kept in sync with airship/apps/features/permissions.py#Feature
export enum Features {
  MessageNav = 'has_messages_nav_access',
  MessageOverview = 'has_messages_overview',
  ContentTemplates = 'has_content_templates_access',
  PushTemplates = 'has_push_templates_access',
  AppleNewsComposer = 'has_apple_news_composer_access',
  ActivityLog = 'has_activity_log_access',
  PushComposer = 'has_pcomposer_access',
  MessageCenter = 'has_message_center_enabled',
  Configuration = 'has_configuration_access',
  JourneysRead = 'has_journeys_read_access',
  SettingsPage = 'has_settings_page_access',
  Reports = 'has_reports_access',
  ReportsOptin = 'has_optin_access',
  ReportsResponse = 'has_response_report_access',
  ReportsAudience = 'has_audience_reports_access',
  ReportsEmail = 'has_email_reports_access',
  ReportsSms = 'has_sms_reports_access',
  ReportsWeb = 'has_web_push_reports_access',
  SmsWorkflows = 'has_sms_workflows_access',
  Survey = 'has_survey_access',
  Darkmode = 'has_dark_mode_access',
  CustomEventsRead = 'has_custom_events_read_access',
  Insight = 'has_insight_access',
  SegmentsRead = 'has_segments_read_access',
  StaticLists = 'has_static_lists_access',
  Tags = 'has_tags_page_access',
  DeviceLookup = 'has_device_lookup_access',
  SubscriptionListAutoOptIn = 'has_subscription_list_auto_optin',
  SubscriptionListActions = 'has_subscription_list_actions',
  MultiChannelPreferenceCenters = 'has_multichannel_preference_centers',
  AiEnabled = 'has_ai_enabled',
  Scene = 'has_scene_access',
  FeatureFlags = 'has_feature_flags_access',
  FeatureFlagsEntitlements = 'has_feature_flags_entitlements',
  UnsubscribePages = 'has_unsubscribe_pages',
  HoldoutExperiments = 'has_holdout_experiments_access',
}

export type ResolveFeaturesResponse<T extends Features> = {
  features: Record<T, boolean>
}

export function createFeaturesResolver<T extends Features>(
  features: Readonly<T[]>
): Resolver<ResolveFeaturesResponse<T>> {
  const resolveFeatures: Resolver<Record<T, boolean>> = (data: unknown) => {
    if (!(data instanceof Object)) {
      throw new ResolveError([
        {
          message: `Expected Object, got ${typeof data}`,
          location: '',
          value: data,
        },
      ])
    }
    for (const feature of features) {
      const value = (data as Record<T, boolean>)[feature]
      if (typeof value !== 'boolean') {
        throw new ResolveError([
          {
            message: `Expected Boolean, got ${typeof value}`,
            location: '',
            value: data,
          },
        ])
      }
    }

    return data as Record<T, boolean>
  }
  return objectResolver({
    features: resolveFeatures,
  })
}
